import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Dropdown from "../../../../parts/dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  describeEmails,
  isOn,
  openRatePercent,
} from "../../../../utils/email-automations";
import { openModal } from "../builder/save-changes";
import TriggerSummary from "./TriggerSummary";

const builderUrl = (automation) =>
  `/coaching-portal/custom-workflows/automations/${automation.id}`;

const StatusChip = ({ status }) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <span className={`ea-status ea-status--${status}`}>
      {t(`emailAutomations.status.${status}`)}
    </span>
  );
};

StatusChip.propTypes = { status: PropTypes.string.isRequired };

const AutomationMenu = ({ automation }) => {
  const { t } = useTranslation("coaching-portal");
  const item = (icon, key, modifier = "") => (
    <span
      className={`ea-menu-item ${modifier}`}
      title={
        modifier === "ea-menu-item--soon"
          ? t("emailAutomations.menu.soon")
          : undefined
      }
    >
      <SvgIcon
        src={`/img/new-icons/${icon}.svg`}
        useCurrentColor
        classNames="icon-sm"
      />
      {t(`emailAutomations.menu.${key}`)}
    </span>
  );
  const options = [
    { text: item("edit-pencil", "edit"), value: "edit" },
    { text: item("duplicate", "duplicate"), value: "duplicate" },
    {
      text: item("file-regular-full", "saveAsTemplate"),
      value: "template",
    },
    {
      text: item("activity-line", "activityLog"),
      value: "activity",
    },
    { separator: true, value: "separator" },
    {
      text: item("red-trash", "delete", "ea-menu-item--danger"),
      value: "delete",
    },
  ];
  const onSelect = (action) => {
    if (action === "template") {
      openModal("saveAsTemplate", { automation: automation.id });
    } else if (action === "activity") {
      submit(`${builderUrl(automation)}?tab=activity`, { method: "GET" });
    } else if (action === "edit") {
      submit(builderUrl(automation), { method: "GET" });
    } else if (action === "duplicate") {
      submit(`/actions/automations/${automation.id}/duplicate`, {
        method: "POST",
        body: {},
      });
    } else {
      openModal("deleteAutomation", { automation: automation.id });
    }
  };
  return (
    <Dropdown
      summaryText="···"
      options={options}
      onSelectFn={onSelect}
      overlay
      alignRight
    />
  );
};

AutomationMenu.propTypes = { automation: PropTypes.object.isRequired };

const AutomationToggle = ({ automation }) => {
  const { t } = useTranslation("coaching-portal");
  const onChange = () => {
    if (isOn(automation)) {
      openModal("turnOffAutomation", { automation: automation.id });
      return;
    }
    openModal("turnOnAutomation", { automation: automation.id });
  };
  return (
    <input
      type="checkbox"
      role="switch"
      checked={isOn(automation)}
      aria-checked={isOn(automation)}
      onChange={onChange}
      aria-label={t("emailAutomations.card.toggle", { name: automation.name })}
    />
  );
};

AutomationToggle.propTypes = { automation: PropTypes.object.isRequired };

const Stats = ({ automation }) => {
  const { t } = useTranslation("coaching-portal");
  if (!automation.sent30) {
    return (
      <span className="ea-card__stats">
        {t("emailAutomations.card.notSentYet")}
      </span>
    );
  }
  const rate = openRatePercent(automation);
  return (
    <span className="ea-card__stats">
      {rate === null
        ? t("emailAutomations.card.sentOnly", { sent: automation.sent30 })
        : t("emailAutomations.card.sentOpened", {
            sent: automation.sent30,
            rate,
          })}
    </span>
  );
};

Stats.propTypes = { automation: PropTypes.object.isRequired };

const AutomationCard = ({ automation, options }) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <article className="ea-card">
      <header>
        <h4>{automation.name}</h4>
        <StatusChip status={automation.status} />
        <AutomationMenu automation={automation} />
      </header>
      <TriggerSummary automation={automation} options={options} />
      <ul className="ea-tags">
        {describeEmails(automation).map((tag) => (
          <li key={tag.type}>
            {t(`emailAutomations.tags.${tag.type}`, { count: tag.count })}
          </li>
        ))}
      </ul>
      <footer>
        <Stats automation={automation} />
        <a href={builderUrl(automation)}>
          {t("emailAutomations.card.editWorkflow")}
        </a>
        <AutomationToggle automation={automation} />
      </footer>
    </article>
  );
};

AutomationCard.propTypes = {
  automation: PropTypes.object.isRequired,
  options: PropTypes.object,
};

export default AutomationCard;
